import React, { useMemo } from "react";
import {
	JobCardData,
	JobStatesValues,
	JobState,
	APPLIED,
	CONFIRMED,
	RECRUITERINTERVIEW,
	HIRINGMANAGERINTERVIEW,
	DONE,
	xlate
} from "../../../DataModels/DataModels";
import JobStateColumn from "./JobStateColumn";
import Carousel, { JustifyEnum } from "../Tabs/Carousel";
import { toTitleCase } from "../../../Utilities/ToTitleCase";

const ERROR_CUT_OFF = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

const errorOnExpiration = (job: JobCardData): JobCardData => {
	const now = Date.now();
	const creationTime = new Date(job.creationTimestamp).getTime();

	return { ...job, isError: job.isError || now - creationTime > ERROR_CUT_OFF };
};

const applyJobRules = (jobs: JobCardData[]): JobCardData[] => {
	return jobs.map(errorOnExpiration);
};

interface CardPanelProps {
	jobs: JobCardData[];
	className?: string;
	isDemo?: boolean;
}

//w-1280px is the mobile mode
const slides: JobState[] = [
	APPLIED,
	CONFIRMED,
	RECRUITERINTERVIEW,
	HIRINGMANAGERINTERVIEW
];

const CardPanel: React.FC<CardPanelProps> = ({
	jobs,
	className = "",
	isDemo = false
}) => {
	const shouldCollapse = window.innerWidth < 1360;
	const structuredJobs = useMemo(() => {
		const jobsWithRules = applyJobRules(jobs);
		return {
			[APPLIED]: [...jobsWithRules].filter(
				(job: JobCardData) => job.state === APPLIED
			),
			[CONFIRMED]: [...jobsWithRules].filter(
				(job: JobCardData) => job.state === CONFIRMED
			),
			[RECRUITERINTERVIEW]: [...jobsWithRules].filter(
				(job: JobCardData) => job.state === RECRUITERINTERVIEW
			),
			[HIRINGMANAGERINTERVIEW]: [...jobsWithRules].filter(
				(job: JobCardData) => job.state === HIRINGMANAGERINTERVIEW
			),
			[DONE]: []
		};
	}, [jobs]);

	if (JobStatesValues.length !== 5) {
		throw new Error(
			`Unexpected number of elements in JobStatesValues: expected 5, but found ${JobStatesValues.length}!`
		);
	}

	const tailWindHeight = "h-[calc(100vh-350px)]";
	const columnWidth = "sm:w-80 md:w-90";

	// Define how each slide should be rendered
	const renderNarrowJobStateColumn = (state: JobState, index: number) => (
		<div
			key={index}
			className={`flex justify-center justify-full gap-4 ${className}`}
		>
			<JobStateColumn
				key={index}
				renderTutorial={jobs.length === 0}
				title={toTitleCase(xlate[state])}
				className={`w-72 ${tailWindHeight}`}
				jobCards={structuredJobs[state]}
				isDemo={isDemo}
			/>
		</div>
	);

	return (
		<>
			{/*Wide Version */}
			{!shouldCollapse && (
				<div className={`flex justify-center justify-full gap-4 ${className}`}>
					{JobStatesValues.filter((state: JobState) => state !== DONE).map(
						(state: JobState, index: number) => {
							return (
								<div key={index} className={columnWidth}>
									<JobStateColumn
										key={index}
										renderTutorial={jobs.length === 0 && state === APPLIED}
										title={toTitleCase(xlate[state])}
										className={tailWindHeight}
										jobCards={structuredJobs[state]}
										isDemo={isDemo}
									/>
								</div>
							);
						}
					)}
				</div>
			)}

			{/*Narrow Version */}
			{shouldCollapse && (
				<Carousel
					justification={JustifyEnum.center}
					slides={slides}
					onRenderSlide={renderNarrowJobStateColumn}
				/>
			)}
		</>
	);
};

export default CardPanel;
